interface FlatTreeItem {
  id: number
  parentId: number | null
  sortOrder?: number
}
type PrimeReactTreeNode<T extends FlatTreeItem> = {
  key: string | number
  data: T
  children: PrimeReactTreeNode<T>[] // *** NO QUESTION MARK ***
  leaf?: boolean
}

const treeNodeSortFn = <T extends FlatTreeItem>(
  a: PrimeReactTreeNode<T>,
  b: PrimeReactTreeNode<T>,
) =>
  typeof a.data.sortOrder === 'number' && typeof b.data.sortOrder === 'number'
    ? a.data.sortOrder - b.data.sortOrder
    : 0

/**
 * Efficiently convert a flat array of records<T> into a PrimeReact TreeNode array
 */
export const flatToPrimeReactTree = <T extends FlatTreeItem>(
  flatControlItems: T[],
): PrimeReactTreeNode<T>[] => {
  const tree: PrimeReactTreeNode<T>[] = []
  const map = new Map<string | number, PrimeReactTreeNode<T>>()

  // 1. Map and Pre-initialization (O(N))
  const treeNodes: PrimeReactTreeNode<T>[] = flatControlItems.map((item) => ({
    key: item.id,
    data: item,
    children: [], // Initialized as an empty array
  }))

  for (const node of treeNodes) {
    map.set(node.key, node)
  }

  // 2. Build Hierarchy (O(N) - Simplified)
  for (const node of treeNodes) {
    const parentId = node.data.parentId

    if (parentId !== null) {
      // It has a parent
      const parent = map.get(parentId)
      if (!parent) throw new Error('parentId not found', { cause: { treeNodes, node } })
      parent.children.push(node)
    } else {
      // It's a root node
      tree.push(node)
    }
  }

  // 3. Sorting and Leaf prop Assignment (O(N log N) total) - by now children is already correctly populated
  for (const node of treeNodes) {
    if (node.children.length > 0) {
      node.children.sort(treeNodeSortFn)
    } else {
      node.leaf = true
    }
  }

  // 4. Sort top-level tree
  tree.sort(treeNodeSortFn)

  return tree
}
